import * as React from 'react';
import { createChangeEvent, CustomTarget, SimulatedChangeEvent } from './useForm';
import { getFileNameExtension, splitReducer } from '../utils';
import { getFileType } from '../lib/mime';
import { useSafeState } from './useSafeState';
import { checksum } from '../lib/checksum';

export type FileInputValue = {
  url: string; // when local, this is a file system url
  signedId: string;
  mime?: string;
  file: File | null;
  extension: string;
  local: boolean;
  checksum?: string;
};

export interface FileInputTarget extends CustomTarget {
  name: string;
  type: 'fileinput';
  value: FileInputValue[];
}

type UseFileInputProps = {
  name: string;
  acceptedFileExtensions: string[];
  inputRef: React.RefObject<HTMLInputElement>;
  maxSize: number;
  minSize?: number;
  onChange: (event: SimulatedChangeEvent<FileInputTarget>) => void;
  value?: FileInputValue[];
  visited?: boolean;
};

const useFileInput = ({
  name,
  acceptedFileExtensions,
  inputRef,
  maxSize,
  minSize = 0,
  onChange,
  value: filesLoaded
}: UseFileInputProps): {
  handleFileUpload: React.ChangeEventHandler<HTMLInputElement>;
  deleteFile: (index: number) => void;
  rejectedFiles: FileInputValue[];
  setRejectedFiles: (newState: React.SetStateAction<FileInputValue[]>) => void;
} => {
  const [rejectedFiles, setRejectedFiles] = useSafeState<FileInputValue[]>([]);
  const handleFileUpload: React.ChangeEventHandler<HTMLInputElement> = async event => {
    const fileList = event.target.files;
    if (fileList && fileList.length > 0) {
      const fileValues = await Promise.all(
        Array.from(fileList).map(getFormAnswerFromFile)
      );
      const [accepted, rejected] = fileValues.reduce(
        splitReducer(isFileAllowed(maxSize, minSize, acceptedFileExtensions)),
        [[], []]
      );

      onChange(
        createChangeEvent({
          type: 'fileinput',
          name,
          value: accepted.concat(filesLoaded)
        })
      );
      setRejectedFiles(rejected);
    }
  };

  const deleteFile = (index: number) => {
    inputRef.current.value = null; // Otherwise you won't be able to reupload the same file if accidentally deleted.
    onChange(
      createChangeEvent({
        type: 'fileinput',
        name,
        value: filesLoaded.filter((_, i) => i !== index)
      })
    );
  };

  return { handleFileUpload, deleteFile, rejectedFiles, setRejectedFiles };
};

export const getFormAnswerFromFile = async (file: File): Promise<FileInputValue> =>
  checksum(file).then(({ base64 }) =>
    getFileType(file).then(({ mime, extension }) => ({
      url: URL.createObjectURL(file),
      signedId: undefined,
      file,
      mime: mime,
      // byte content > File type reported > filename extension
      extension: extension || getFileNameExtension(file.name),
      local: true,
      checksum: base64
    }))
  );

const isFileAllowed =
  (maxSize: number, minSize: number, acceptedFileExtensions: string[]) =>
  (fileValue: FileInputValue) =>
    fileValue.file.size <= maxSize &&
    fileValue.file.size >= minSize &&
    acceptedFileExtensions.includes(fileValue.extension.toLowerCase());

export { useFileInput };
